import type { FieldArrayWithId } from 'react-hook-form'
import { clsx } from 'clsx'
import { fieldLabels } from '@/features/consignment-form/labels'
import { estimateKey } from '@/features/consignment-form/estimate'
import type { EstimateState } from '@/features/consignment-form/estimate'
import { productTitle } from '@/features/consignment-form/products'
import type { Product } from '@/features/consignment-form/products'
import type { Consignment, Item } from '@/features/consignment-form/schema'
import { ItemEditor } from '@/features/consignment-form/sections/ItemEditor'
import { t } from '@/lang'
import { Button } from '@/ui/Button'
import { PencilIcon } from '@/ui/icons/PencilIcon'
import { PlusIcon } from '@/ui/icons/PlusIcon'
import { Section } from '@/ui/Section'
import { testIdHelpers, testIds } from '@/lib/test-ids'

const tableColumns = 'lg:grid-cols-[114fr_353fr_228fr_114fr_171fr] lg:gap-10'
const label = 'text-end text-row-label text-secondary-600 lg:hidden'
const pairs = 'contents md:grid md:grid-cols-[auto_1fr] md:items-baseline md:gap-x-2.5 md:gap-y-2.5 lg:contents'
const estimate =
  'contents md:flex md:flex-col md:items-center md:gap-2.5 lg:flex lg:flex-row lg:items-center lg:justify-between lg:gap-5'
const estimatePair = 'contents md:flex md:items-baseline md:gap-2.5 lg:contents'
const pending = t('common.no-value')

const measurements = (item: Item) =>
  [
    item.weightKg === '' ? undefined : t('items.weight', { weight: item.weightKg }),
    item.lengthCm === ''
      ? undefined
      : t('items.size', { length: item.lengthCm, width: item.widthCm, height: item.heightCm }),
  ]
    .filter((line) => line !== undefined)
    .join('\n') || pending

type Props = {
  products: readonly Product[]
  items: FieldArrayWithId<Consignment, 'items'>[]
  estimates: Record<string, EstimateState>
  editingIndex: number | null
  adding: boolean
  draftItem?: Item
  onEdit: (index: number) => void
  onAdd: () => void
  onSave: (values: Item) => Promise<void>
  onDraftChange: (values: Item) => void
  onDelete: () => void
  onCancel: () => void
}

export function ItemsSection({
  products,
  items,
  estimates,
  editingIndex,
  adding,
  draftItem,
  onEdit,
  onAdd,
  onSave,
  onDraftChange,
  onDelete,
  onCancel,
}: Props) {
  const editing = editingIndex !== null
  const editorOpen = adding || editing
  const rowsShown = items.some((_, index) => index !== editingIndex)

  return (
    <Section title={fieldLabels.items} required>
      {rowsShown ? (
        <div
          data-testid={testIds.consignmentForm.itemsHeader}
          className={clsx('hidden px-5 text-table-header text-secondary-600 lg:grid', tableColumns)}
        >
          <span>{t('items.column.packages')}</span>
          <span>{t('items.column.description')}</span>
          <span>{t('items.column.measurements')}</span>
          <span>{t('items.column.cubic')}</span>
        </div>
      ) : null}
      {items.map((item, index) => {
        const quote = estimates[estimateKey(item)]
        return index === editingIndex ? (
          <ItemEditor
            key={item.id}
            products={products}
            item={draftItem ?? item}
            onSave={onSave}
            onDraftChange={onDraftChange}
            onDelete={onDelete}
            onCancel={onCancel}
          />
        ) : (
          <div
            key={item.id}
            data-testid={testIdHelpers.listItem('consignment-item', index)}
            className={clsx(
              'mt-2.5 min-h-19 rounded-sm bg-white px-5 py-5 text-neutral-800',
              'grid grid-cols-[auto_minmax(0,11rem)] items-baseline justify-center gap-2.5',
              'md:grid-cols-[300fr_230fr_118fr] md:items-start md:gap-x-7.5',
              'lg:grid lg:items-center lg:py-0',
              tableColumns
            )}
          >
            <div className={pairs}>
              <span className={label}>{t('items.column.packages')}</span>
              <span>{item.qty}</span>
              <span className={label}>{t('items.column.description')}</span>
              <span className="whitespace-pre-line">
                {productTitle(item.category, products)}:{'\n'}
                {item.description}
              </span>
            </div>
            <div className={pairs}>
              <span className={label}>{t('items.column.measurements')}</span>
              <span className="whitespace-pre-line">{measurements(item)}</span>
              <span className={label}>{t('items.column.cubic')}</span>
              <span>
                {quote?.status === 'ready' && quote.value.cubicInM3 !== undefined
                  ? t('items.cubic', { cubic: quote.value.cubicInM3 })
                  : pending}
              </span>
            </div>
            <div className={estimate}>
              <span className={estimatePair}>
                <span className={label}>{t('items.estimate')}</span>
                <span className="font-bold">{quote?.status === 'ready' ? quote.value.formattedAmount : pending}</span>
              </span>
              <Button
                aria-label={t('items.edit-label', { number: index + 1 })}
                icon={<PencilIcon className="w-4 shrink-0" />}
                disabled={editorOpen}
                onClick={() => onEdit(index)}
                className="col-span-2 justify-self-center max-md:mt-2.5 md:max-lg:w-full lg:size-9 lg:gap-0 lg:px-0"
              >
                <span className="lg:hidden">{t('items.edit')}</span>
              </Button>
            </div>
          </div>
        )
      })}

      {adding && !editing ? (
        <ItemEditor
          key={draftItem ? 'restored' : 'new'}
          products={products}
          item={draftItem}
          onSave={onSave}
          onDraftChange={onDraftChange}
          onCancel={items.length > 0 ? onCancel : undefined}
        />
      ) : null}

      {editorOpen ? null : (
        <div className="mt-5 max-md:text-center">
          <Button variant="secondary" size="md" icon={<PlusIcon className="w-4 shrink-0" />} onClick={onAdd}>
            {t('items.add')}
          </Button>
        </div>
      )}
    </Section>
  )
}
